import React, { useMemo } from "react";
import { Button } from "react-bootstrap";
import { HiArrowLeft, HiOutlineCalendarDays, HiOutlineCheckBadge, HiOutlineDocumentText, HiOutlineUserCircle } from "react-icons/hi2";
import ContentWrapper from "../../../components/common/ContentWrapper";
import Card from "../../../components/common/Card";
import CardBody from "../../../components/common/CardBody";
import { routes } from "../../../helper/routes";
import "./PaperMappingDetails.css";

const STORAGE_KEY = "paper-mapping-detail-view";

const readStoredPayload = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

export default function PaperMappingDetails(props) {
  const routeState = props.location?.state?.paperDetail;
  const assessmentId = props.match?.params?.assessmentId;

  const detail = useMemo(() => {
    const stored = readStoredPayload();
    if (routeState && String(routeState.assessmentId) === String(assessmentId)) {
      return routeState;
    }
    if (stored && String(stored.assessmentId) === String(assessmentId)) {
      return stored;
    }
    return routeState || stored;
  }, [assessmentId, routeState]);

  const handleBack = () => {
    if (props.history?.length > 1) {
      props.history.goBack();
      return;
    }
    props.history.push(routes.paper_mapping);
  };

  if (!detail) {
    return (
      <ContentWrapper>
        <div className="container-fluid paper-detail-page">
          <div className="paper-detail-shell">
            <Button
              variant="link"
              className="paper-detail-back-btn"
              onClick={handleBack}
              aria-label="Go back"
            >
              <HiArrowLeft />
            </Button>
            <Card>
              <CardBody>
                <div className="paper-detail-empty">
                  <HiOutlineDocumentText className="paper-detail-empty-icon" />
                  <h2 className="paper-detail-empty-title">Paper details are not available</h2>
                  <p className="paper-detail-empty-text">
                    Return to the paper list and open the paper again.
                  </p>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper>
      <div className="container-fluid paper-detail-page">
        <div className="paper-detail-shell">
          <div className="paper-detail-hero">
            <div className="row align-items-start paper-detail-hero-row">
              <div className="col-auto paper-detail-back-col">
                <Button
                  variant="link"
                  className="paper-detail-back-btn"
                  onClick={handleBack}
                  aria-label="Go back"
                >
                  <HiArrowLeft />
                </Button>
              </div>
              <div className="col">
                <div className="paper-detail-hero-content">
                  <span className="paper-detail-overline">Paper details</span>
                  <h1 className="paper-detail-title">{detail.title}</h1>
                  <div className="paper-detail-meta">
                    <span className="paper-detail-meta-pill">
                      <HiOutlineDocumentText />
                      {detail.paperCode}
                    </span>
                    <span className={`paper-detail-status-pill paper-status-${String(detail.assessmentState || "").toLowerCase()}`}>
                      <HiOutlineCheckBadge />
                      {detail.assessmentState || "N/A"}
                    </span>
                    {detail.isAdded && (
                      <span className="paper-detail-added-pill">
                        <HiOutlineCheckBadge />
                        Added
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-12 col-lg-4 col-xl-3">
                <div className="paper-detail-summary">
                  <div className="paper-detail-summary-item">
                    <HiOutlineUserCircle />
                    <div>
                      <span className="paper-detail-summary-label">Created by</span>
                      <strong>{detail.createdBy || "N/A"}</strong>
                    </div>
                  </div>
                  <div className="paper-detail-summary-item">
                    <HiOutlineCalendarDays />
                    <div>
                      <span className="paper-detail-summary-label">Created at</span>
                      <strong>{detail.createdAt || "N/A"}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Card className="paper-detail-card">
            <CardBody>
              <div className="row">
                {detail.fields?.map((field) => (
                  <div className="col-12 col-md-6 col-xl-4 mb-4" key={field.label}>
                    <div className="paper-detail-field">
                      <span className="paper-detail-field-label">{field.label}</span>
                      <span className="paper-detail-field-value">{field.value || "N/A"}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </ContentWrapper>
  );
}
